import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";

import { Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import { DeeplinkComponent } from "../../components/deeplink";
import { DeeplinkModal } from "../../components/modals";
import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { CurrencySelector, DateRange, MarketSelector } from "../../components/selectors";
import { ColumnTitle, TableNotification } from "../../components/table";
import { reportGeneratorTaskCreateRequest, tableFetchRequest } from "../../store/clicks/actions";
import { ClickItem } from "../../store/clicks/types";
import { Deeplink, ReportFormat } from "../../store/common_types";
import { selectCurrency } from "../../store/currencies/actions";
import { RootState } from "../../store/root_reducer";
import { MomentParam } from "../../utils/moment_param";
import { formatNumber, getSortOrder } from "../../utils/table";

export const Clicks: React.FC = () => {
  const dispatch = useDispatch();

  const [dateRange, setDateRange] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
  });

  const [pagination, setPagination] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
  });

  const [ordering, setOrdering] = useQueryParam("ordering", withDefault(StringParam, "-createdAt"));

  const [market, setMarket] = useQueryParam("market", withDefault(StringParam, undefined));
  useEffect(() => {
    setDateRange((state) => state, "replaceIn");
    setPagination((state) => state, "replaceIn");
    setOrdering((state) => state, "replaceIn");
    setMarket((state) => state, "replaceIn");
  }, [setDateRange, setMarket, setOrdering, setPagination]);

  const markets = useSelector((state: RootState) => state.Markets.markets);

  const currency = useSelector((state: RootState) => state.Currencies.selectedCurrency);
  const chooseCurrency = (value: string) => dispatch(selectCurrency({ currency: value }));
  const onDateRangeSelect = (value: DateRange) => {
    setDateRange(value);
    setPagination({ ...pagination, page: 1 }, "replaceIn");
  };

  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);

  const fetch = useCallback(() => {
    dispatch(
      tableFetchRequest({
        ...pagination,
        ...dateRange,
        market,
        currency,
        ordering,
        gateId: selectedGate.id,
      })
    );
  }, [dispatch, dateRange, pagination, market, currency, ordering, selectedGate]);

  const downloadReport = useCallback(
    (reportFormat: ReportFormat) => {
      dispatch(
        reportGeneratorTaskCreateRequest({
          ...dateRange,
          market,
          currency,
          ordering,
          reportFormat,
          gateId: selectedGate.id,
        })
      );
    },
    [dispatch, dateRange, market, currency, ordering, selectedGate]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  const [deeplinkModal, setDeeplinkModal] = useState<{ visible: boolean; deeplink: Deeplink | undefined }>({
    visible: false,
    deeplink: undefined,
  });

  const handleDeeplinkClick = (deeplink: Deeplink) => {
    setDeeplinkModal({ visible: true, deeplink });
  };

  const handleDeeplinkModalClose = () => {
    setDeeplinkModal((state) => ({ ...state, visible: false }));
  };

  const { t } = useTranslation(["clicks_page", "common"]);
  const columns = [
    {
      title: <ColumnTitle title={t("table.columns.date.title")} helpText={t("table.columns.date.help")} />,
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder: getSortOrder("createdAt", ordering),
    },
    {
      title: t("table.columns.id.title"),
      dataIndex: "marker",
      key: "marker",
    },
    {
      title: <ColumnTitle title={t("table.columns.unique.title")} helpText={t("table.columns.unique.help")} />,
      key: "isUnique",
      dataIndex: "isUnique",
      render: (text: string, item: ClickItem) => t(`table.columns.unique.options.${item.isUnique ? "yes" : "no"}`),
    },
    {
      title: (
        <ColumnTitle title={t("table.columns.price.title")} helpText={t("table.columns.price.help", { currency })} />
      ),
      key: "proposalPrice",
      dataIndex: "proposalPrice",
      sorter: true,
      align: 'center' as const,
      sortOrder: getSortOrder("proposalPrice", ordering),
      render: (value: number) => `${formatNumber(value)} ${currency.toUpperCase()}`,
    },
    {
      title: <ColumnTitle title={t("table.columns.gatePrice.title")} helpText={t("table.columns.gatePrice.help")} />,
      key: "gatePrice",
      dataIndex: "gatePrice",
      align: 'center' as const,
      render: (_: number, record: ClickItem) => 
        `${formatNumber(record.gatePriceAmount)} ${record.gatePriceCurrency.toUpperCase()}`
    },
    {
      title: <ColumnTitle title={t("table.columns.host.title")} helpText={t("table.columns.host.help")} />,
      key: "referrerHost",
      dataIndex: "referrerHost",
    },
    {
      title: <ColumnTitle title={t("table.columns.deeplink.title")} helpText={t("table.columns.deeplink.help")} />,
      key: "deeplink",
      dataIndex: "deeplink",
      render: (deeplink?: Deeplink) =>
        deeplink && <DeeplinkComponent deeplink={deeplink} onDeeplinkClick={handleDeeplinkClick} />,
    },
    {
      title: <ColumnTitle title={t("table.columns.ip.title")} helpText={t("table.columns.ip.help")} />,
      key: "searchUserIp",
      dataIndex: "searchUserIp",
    },
    {
      title: t("table.columns.origin.title"),
      render: (text: string, record: ClickItem) => {
        let from = record.origin0;
        if (record.origin1) {
          from += `,${record.origin1}`;
        }
        return from;
      },
      key: "from",
      dataIndex: "from",
    },
    {
      title: t("table.columns.destination.title"),
      render: (text: string, record: ClickItem) => {
        let from = record.destination0;
        if (record.destination1) {
          from += `,${record.destination1}`;
        }
        return from;
      },
      key: "to",
      dataIndex: "to",
    },
    {
      title: <ColumnTitle title={t("table.columns.prediction.title")} helpText={t("table.columns.prediction.help")} />,
      key: "prediction",
      dataIndex: "prediction",
      render: (text: string, item: ClickItem) => item.prediction?.toFixed(5),
    },
    {
      title: t("table.columns.platform.title"),
      key: "platform",
      dataIndex: "platform",
      render: (value: string) => t(`common:platforms.${value}`),
    },
    {
      title: t("table.columns.market.title"),
      key: "market",
      dataIndex: "market",
    },
    {
      title: t("table.columns.mainAirline.title"),
      key: "mainAirline",
      dataIndex: "mainAirline",
    },
    {
      title: t("table.columns.isMixedFlights.title"),
      key: "isMixedFlights",
      dataIndex: "isMixedFlights",
      render: (text: string, item: ClickItem) =>
        t(`table.columns.unique.options.${item.isMixedFlights ? "yes" : "no"}`),
    },
  ];

  const reportState = useSelector((state: RootState) => state.Clicks);

  return (
    <Page title={t("title")} description={t("description")} reportTTL={365} report="clicks">
      <PageHeader
        onReload={fetch}
        dateRange={dateRange}
        isLoading={reportState.isLoading}
        onDownloadReport={downloadReport}
        onDateRangeSelect={onDateRangeSelect}
        reportDownloadDisabled={reportState.count === 0}
      >
        <MarketSelector
          markets={markets}
          value={market}
          onSelect={(value) => {
            setPagination({ ...pagination, page: 1 });
            setMarket(value, "replaceIn");
          }}
        />
        <CurrencySelector value={currency} onSelect={chooseCurrency} />
      </PageHeader>
      <TableNotification />
      <DeeplinkModal onClose={handleDeeplinkModalClose} {...deeplinkModal} />
      <Table
        bordered
        columns={columns}
        size="small"
        loading={reportState.isLoading}
        dataSource={reportState.tableData}
        rowKey={(record: ClickItem) => `clicks-row-${record.marker}`}
        pagination={{
          total: reportState.count,
          current: pagination.page,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "100"],
          onChange: (page, pageSize) => {
            if (page && pageSize) {
              setPagination((currentPagination) => ({
                ...currentPagination,
                pageSize,
                page: pageSize !== currentPagination.pageSize ? 1 : page,
              }));
            }
          },
        }}
        showSorterTooltip={false}
        sortDirections={["ascend", "descend", "ascend"]}
        onChange={(_, filters, sorter: SorterResult<ClickItem> | SorterResult<ClickItem>[], extra) => {
          if (!Array.isArray(sorter)) {
            setOrdering(`${sorter.order === "descend" ? "-" : ""}${sorter.column?.dataIndex}`);
          }
        }}
      />
    </Page>
  );
};
